import type { AppContext } from 'types/common'
import type { TeardownScreen$result } from '$houdini'
import type { WritableDeep } from 'type-fest'

import component from './Teardown.svelte'
import { graphql } from '$houdini'
import { hasPermission } from 'utility/permission'
import { getDefaultTemplateId } from 'utility/teardown-list'

export type TeardownVehicle = WritableDeep<NonNullable<TeardownScreen$result['vehicle']>>
export type TeardownInventory = TeardownVehicle['inventories'][number]
export type TeardownTemplate = TeardownVehicle['wholeUnit']['inventoryType']['breakdownTemplates'][number]
export type TeardownInventoryType = WritableDeep<TeardownScreen$result['inventoryTypes']>[number]
export type TeardownFrequentlyUsedInventoryType = TeardownScreen$result['frequentlyUsedInventoryTypes'][number]

const teardownScreenQuery = graphql(`
	query TeardownScreen($vehicleId: Int!, $storeId: UInt!) {
		vehicle(id: $vehicleId) {
			id
			storeId
			status
			wholeUnit {
				inventoryType {
					breakdownTemplates {
						id
						name
						default
						components {
							inventoryTypeId
							quantity
							rank
							integral
						}
					}
				}
			}
			inventories {
				innodbInventoryid: id
				inventoryId
				storeId
				tagNumber
				status
				inventoryTypeId
				retailPrice
				cost
				manufacturer {
					name
				}
				model {
					name
				}
			}
		}
		inventoryTypes {
			id
			name
			active
		}
		frequentlyUsedInventoryTypes: inventoryTypesFrequentlyUsed(storeId: $storeId) {
			id
			name
		}
		session {
			fieldAccess {
				inventory {
					cost {
						read
					}
				}
			}
		}
	}
`)

export default function createState({ stateRouter, i18next: { t: translate } }: AppContext) {
	stateRouter.addState({
		name: 'app.vehicle.teardown',
		route: 'teardown',
		// `selectedItem` and `templateId` aren't written when the user clicks around, only when the screen reloads itself (after a save), so they survive the reload.
		querystringParameters: ['vehicleId', 'storeId', 'selectedItem', 'templateId'],
		defaultParameters: {
			selectedItem: null,
			templateId: null,
		},
		template: {
			svelte: true,
			component,
		},
		// The router passes the destination as a second argument, which its types leave out
		canLeaveState(domApi, destination?: { name: string; parameters: Record<string, unknown> }) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
			return domApi.canLeaveState(destination)
		},
		async resolve(_data, parameters) {
			const vehicleId = parseInt(parameters.vehicleId, 10)
			const storeId = parseInt(parameters.storeId, 10)

			if (!vehicleId || !hasPermission('VEHICLES_CAN_EDIT_BIDS', storeId)) {
				alert(translate('vehicle.youDoNotHavePermissionToUseTeardown', 'You do not have permission to use Teardown.'))
				throw {
					redirectTo: {
						name: 'app.vehicle.basic',
						params: { vehicleId, storeId },
					},
				}
			}

			const { data } = await teardownScreenQuery.fetch({ variables: { vehicleId, storeId } })

			if (!data?.vehicle) {
				throw new Error(translate('vehicle.failedToLoadTeardownData', 'Failed to load teardown data'))
			}

			const vehicle = data.vehicle as TeardownVehicle
			const templates = vehicle.wholeUnit.inventoryType.breakdownTemplates
			// No `templateId` means the user hasn't picked one yet, so use the default. 'none' means they picked "None".
			const templateId = parameters.templateId
				? parseInt(parameters.templateId, 10) || null
				: getDefaultTemplateId(templates)

			return {
				vehicle,
				templates,
				templateId: templates.some(template => template.id === templateId) ? templateId : null,
				inventoryTypes: data.inventoryTypes as Array<TeardownInventoryType>,
				frequentlyUsedInventoryTypes: data.frequentlyUsedInventoryTypes,
				canViewCost: data.session.fieldAccess?.inventory?.cost?.read ?? false,
				selectedItem: parameters.selectedItem || undefined,
			}
		},
	})
}
